import { useEffect, useRef, useState } from 'react';
import { addDays, differenceInMilliseconds, setHours, setMinutes } from 'date-fns';

import { getManifest } from '../../api/services/Manifest';

// How often to poll the manifest while the tab is open. Catches deploys between
// the scheduled hour checks without waiting for a tab focus/blur or navigation.
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
const MAX_RETRIES = 2;
const RETRY_INTERVAL_MS = 5000;

export const useManifest = () => {
  const [isBundleExpired, setIsBundleExpired] = useState(false);
  const isBundleExpiredRef = useRef(false);
  const retriesRef = useRef(0);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return undefined;
    }

    const hoursToCheck = window.env.RIDEIQ_REFRESH_HOURS?.split(',');

    // Run an immediate check on mount so a user who lands on a route with a
    // stale bundle gets the banner without waiting for the next scheduled hour,
    // heartbeat tick, or tab-focus event.
    checkManifest();

    if (hoursToCheck) {
      hoursToCheck.forEach((hour) => {
        checkTime(parseInt(hour));
      });
    }

    const heartbeat = setInterval(() => {
      // Once the banner is visible, stop polling — the banner only re-flips on
      // a fresh page load anyway, so further requests are wasted.
      if (isBundleExpiredRef.current) {
        clearInterval(heartbeat);

        return;
      }
      checkManifest();
    }, HEARTBEAT_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkManifest();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  function checkTime(hour: number, isNextDay = false) {
    const timeNow = new Date();
    const timeAt = setTime(isNextDay ? addDays(timeNow, 1) : timeNow, hour);
    const millisecondsUntilTime = differenceInMilliseconds(timeAt, timeNow);

    const timeout =
      millisecondsUntilTime >= 0
        ? millisecondsUntilTime
        : differenceInMilliseconds(setTime(addDays(timeNow, 1), hour), timeNow);

    setTimeout(async () => {
      await checkManifest();
      checkTime(hour, true); // set the timer again
    }, timeout);
  }

  // Entry point for the four triggers (mount, heartbeat, scheduled hour,
  // visibilitychange). Resets the per-sequence retry budget and dedupes against
  // an in-flight fetch so overlapping triggers don't stomp each other.
  function checkManifest() {
    if (inFlightRef.current) {
      return;
    }
    retriesRef.current = 0;
    performFetch();
  }

  function performFetch() {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    getManifest()
      .then((response) => {
        inFlightRef.current = false;
        if (!response.data?.rideiqJs?.includes(__webpack_hash__)) {
          isBundleExpiredRef.current = true;
          setIsBundleExpired(true);
        }
      })
      .catch(() => {
        inFlightRef.current = false;
        reTryManifest();
      });
  }

  const reTryManifest = () => {
    if (retriesRef.current < MAX_RETRIES) {
      retriesRef.current += 1;
      setTimeout(performFetch, RETRY_INTERVAL_MS);
    }
  };

  return isBundleExpired;
};

function setTime(time, hours) {
  const setHoursForTime = setHours(time, hours);

  return setMinutes(setHoursForTime, 0);
}
